/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { ReactNode } from 'react';
import { Field, FormSpy } from 'react-final-form';
import { css, StyleSheet } from 'aphrodite';

import { BrandColors, composeValidators, Dropdown, isEmptyValue, Text } from '@blend/enterprise-components';

import { ScheduleClosingAppointmentTask } from '../ScheduleClosingAppointmentTask';
import { renderFormTextInput, renderFormSelect } from './ScheduleClosingTaskInput';
import { notarizationPlatformOptions, formatAddressString } from './ScheduleClosingTaskUtils';
import { states } from 'blb-constants/stateCodes';
import { BlendBridge } from 'blb-types/blendBridge';
import { emailValidator } from 'blb-utils/validators';

const styles = StyleSheet.create({
  fieldset: {
    marginBottom: '24px',
  },
  formHeader: {
    color: BrandColors.NIGHT_90,
    fontSize: '14px',
    lineHeight: '20px',
  },
  formLabel: {
    color: BrandColors.NIGHT_90,
    fontSize: '12px',
    lineHeight: '18px',
  },
  fullRow: {
    flexDirection: 'column',
    width: '100%',
  },
  halfRow: {
    ':first-child': {
      marginRight: '16px',
    },
    flexDirection: 'column',
    width: '50%',
  },
  legend: {
    color: BrandColors.NIGHT_90,
    fontSize: '14px',
    lineHeight: '20px',
    marginBottom: '8px',
  },
  notarizationMethodInstructions: {
    color: BrandColors.NIGHT_60,
    fontSize: '12px',
    lineHeight: '14px',
    marginTop: '4px',
  },
  rowContainer: {
    ':last-child': {
      marginBottom: '0px',
    },
    display: 'flex',
    justifyContent: 'flex-start',
    label: {
      marginBottom: '4px',
    },
    margin: 0,
    marginBottom: '16px',
  },
});

/**
 * Renders the Notary selection of RON closings.
 * RON closings are defined by Closing Type being either `BLEND_SIGNING_ROOM` or `NOTARYCAM`.
 * @param  {BlendBridge.API.ScheduleClosingAppointmentTaskClosingType} closingType    Type of Closing
 * @param  {React.Dispatch<React.SetStateAction<string|undefined>>}    errorDispatch  React State Dispatch method
 * @return {ReactNode}                                                                Notarization Selection for RON closings
 * @function
 */
const renderNotarizationPlatformRow = (
  closingType: BlendBridge.API.ScheduleClosingAppointmentTaskClosingType,
  errorDispatch: React.Dispatch<React.SetStateAction<string | undefined>>,
): ReactNode => (
  <div className={css(styles.rowContainer)}>
    <div className={css(styles.fullRow)}>
      {renderFormSelect({
        disabled: true,
        hasPII: false,
        label: 'Remote online notarization platform',
        name: 'appointmentInformation.notarization',
        options: notarizationPlatformOptions(closingType),
        setErrorDispatch: errorDispatch,
        validate: isEmptyValue('Please select a notarization platform.'),
      })}
    </div>
  </div>
);

/**
 * Renders the Notary selection of Non-RON closings.
 * Non-RON closings are defined by Closing Type being one of the following:
 *  - HYBRID
 *  - TRADITIONAL
 *  - UNDETERMINED
 * @param  {ScheduleClosingAppointmentTask.Props}                      props          Props of Parent
 * @param  {React.Dispatch<React.SetStateAction<string | undefined>>}  errorDispatch  React State Dispatch method
 * @return {ReactNode}                                                                Notarization Selection for Non-RON closings
 * @function
 */
const renderNotarizationMethodRow = (
  props: ScheduleClosingAppointmentTask.Props,
  errorDispatch: React.Dispatch<React.SetStateAction<string | undefined>>,
): ReactNode => (
  <>
    {/* Notarization Method Row */}
    <div className={css(styles.rowContainer)}>
      <div className={css(styles.fullRow)}>
        <FormSpy subscription={{ values: true }}>
          {({ values }) => (
            <>
              <Field
                defaultValue={values.appointmentInformation.notarization}
                name="appointmentInformation.notarization"
                validate={isEmptyValue('Please select a notarization method.')}
              >
                {({ input }) => (
                  <>
                    <label className={css(styles.formLabel)} htmlFor={input.name}>
                      Closing method
                    </label>
                    <Dropdown
                      {...input}
                      id={input.name}
                      options={props.notarizationMethodOptions as Dropdown.Option[]}
                      type="text"
                      valueRequired={true}
                    />
                  </>
                )}
              </Field>
              <p className={css(styles.notarizationMethodInstructions)} id="notarization-hint-text">
                {values.appointmentInformation.notarization === 'MOBILE' && props.borrowerPreferredMethod === 'MOBILE' && (
                  <>
                    <Text colorName="NIGHT_90" fontSize="md">
                      The borrower would prefer to meet{' '}
                    </Text>
                    <Text colorName="NIGHT_90" fontSize="md" weight="bold">
                      at
                    </Text>
                    <Text colorName="NIGHT_90" fontSize="md">
                      {' '}
                      the location below.
                    </Text>
                  </>
                )}
                {values.appointmentInformation.notarization === 'IN_OFFICE' &&
                  props.borrowerPreferredMethod === 'IN_OFFICE' &&
                  props.borrowerPreferredAddress !== undefined && (
                    <>
                      <Text colorName="NIGHT_90" fontSize="md">
                        The borrower would prefer to meet{' '}
                      </Text>
                      <Text colorName="NIGHT_90" fontSize="md" weight="bold">
                        near
                      </Text>
                      <Text colorName="NIGHT_90" fontSize="md">
                        {' '}
                        {formatAddressString(props.borrowerPreferredAddress)}.
                      </Text>
                    </>
                  )}
              </p>
            </>
          )}
        </FormSpy>
      </div>
    </div>

    {/* Address Line 1 Row */}
    <div className={css(styles.rowContainer)}>
      <div className={css(styles.fullRow)}>
        {renderFormTextInput({
          hasPII: true,
          inputType: 'text',
          label: 'Address line 1',
          name: 'appointmentInformation.address.addressLineOne',
          setErrorDispatch: errorDispatch,
          validate: isEmptyValue('Please input a street address.'),
        })}
      </div>
    </div>

    {/* Address Line 2 Row */}
    <div className={css(styles.rowContainer)}>
      <div className={css(styles.fullRow)}>
        {renderFormTextInput({
          hasPII: true,
          inputType: 'text',
          label: 'Address line 2 (optional)',
          name: 'appointmentInformation.address.addressLineTwo',
          setErrorDispatch: errorDispatch,
        })}
      </div>
    </div>

    {/* City Row */}
    <div className={css(styles.rowContainer)}>
      <div className={css(styles.fullRow)}>
        {renderFormTextInput({
          hasPII: true,
          inputType: 'text',
          label: 'City',
          name: 'appointmentInformation.address.city',
          setErrorDispatch: errorDispatch,
          validate: isEmptyValue('Please enter a city.'),
        })}
      </div>
    </div>

    {/* State Row */}
    <div className={css(styles.rowContainer)}>
      <div className={css(styles.halfRow)}>
        {renderFormSelect({
          hasPII: true,
          label: 'State',
          name: 'appointmentInformation.address.state',
          options: states,
          setErrorDispatch: errorDispatch,
          validate: isEmptyValue('Please choose a state.'),
        })}
      </div>

      {/* Zip Code Row */}
      <div className={css(styles.halfRow)}>
        {renderFormTextInput({
          hasPII: true,
          inputType: 'text',
          label: 'Zip code',
          name: 'appointmentInformation.address.zipCode',
          setErrorDispatch: errorDispatch,
          validate: composeValidators((val: string) => {
            const err = val && val.length < 5 ? 'Please enter a 5-digit zip code.' : undefined;
            return err;
          }, isEmptyValue('Please enter a zip code.')),
        })}
      </div>
    </div>
  </>
);

/**
 * Renders the Appointment Information aspect of the ScheduleClosingAppointmentTask Form.
 * @param  {ScheduleClosingAppointmentTask.Props} props                             Props of Parent
 * @param  {React.Dispatch<React.SetStateAction<string | undefined>>} errorDispatch React State Dispatch method
 * @return {ReactNode}                                                              Appointment Fieldset of Form
 * @function
 */
export const renderAppointmentInformation = (
  props: ScheduleClosingAppointmentTask.Props,
  errorDispatch: React.Dispatch<React.SetStateAction<string | undefined>>,
): ReactNode => (
  <fieldset className={css(styles.fieldset)}>
    <legend className={css(styles.legend)}>Appointment information</legend>

    {/* Date/Timezone Row */}
    <div className={css(styles.rowContainer)}>
      <div className={css(styles.halfRow)}>
        {renderFormTextInput({
          disabled: !!props.closingDate,
          hasPII: false,
          inputType: 'date',
          label: 'Date',
          name: 'appointmentInformation.date',
          setErrorDispatch: errorDispatch,
          validate: composeValidators((val: string) => {
            // validating that text matches format MM/dd / yyyy;
            const pattern = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
            const err = val && !pattern.test(val) ? 'Please enter a valid date.' : undefined;
            return err;
          }, isEmptyValue('Please provide a date.')),
        })}
      </div>
      <div className={css(styles.halfRow)}>
        {renderFormSelect({
          disabled: !!props.borrowerPreferredTimezone,
          hasPII: false,
          label: 'Timezone',
          name: 'appointmentInformation.timezone',
          options: props.timezoneOptions as Dropdown.Option[],
          placeholder: 'Select timezone',
          setErrorDispatch: errorDispatch,
          validate: isEmptyValue('Please select a timezone.'),
        })}
      </div>
    </div>

    {/* Time Row */}
    <div className={css(styles.rowContainer)}>
      <div className={css(styles.fullRow)}>
        {renderFormSelect({
          hasPII: false,
          label: 'Time',
          name: 'appointmentInformation.time',
          options: props.timeOptions as Dropdown.Option[],
          placeholder: 'Select time',
          setErrorDispatch: errorDispatch,
          validate: isEmptyValue('Please select a time.'),
        })}
      </div>
    </div>

    {/* Notarization Method Row */}
    {(props.closingType === 'BLEND_SIGNING_ROOM' || props.closingType === 'NOTARYCAM') &&
      renderNotarizationPlatformRow(props.closingType, errorDispatch)}
    {(props.closingType === 'HYBRID' || props.closingType === 'TRADITIONAL' || props.closingType === 'UNDETERMINED') &&
      renderNotarizationMethodRow(props, errorDispatch)}
  </fieldset>
);

/**
 * Renders the Notary UI aspect of the ScheduleClosingAppointmentTask Form.
 * @param  {React.Dispatch<React.SetStateAction<string | undefined>>} errorDispatch React State Dispatch method
 * @return {ReactNode}                                                              Notary Fieldset of Form
 * @function
 */
export const renderNotaryInformation = (
  errorDispatch: React.Dispatch<React.SetStateAction<string | undefined>>,
): ReactNode => (
  <fieldset className={css(styles.fieldset)}>
    {/* Fieldset Title */}
    <legend className={css(styles.legend)}>Notary information</legend>

    {/* First/Last Name Row */}
    <div className={css(styles.rowContainer)}>
      <div className={css(styles.halfRow)}>
        {renderFormTextInput({
          hasPII: true,
          inputType: 'text',
          label: 'First name',
          name: 'notaryInformation.firstName',
          setErrorDispatch: errorDispatch,
          validate: isEmptyValue('Please provide a first name.'),
        })}
      </div>
      <div className={css(styles.halfRow)}>
        {renderFormTextInput({
          hasPII: true,
          inputType: 'text',
          label: 'Last name',
          name: 'notaryInformation.lastName',
          setErrorDispatch: errorDispatch,
          validate: isEmptyValue('Please provide a last name.'),
        })}
      </div>
    </div>

    {/* Email Row */}
    <div className={css(styles.rowContainer)}>
      <div className={css(styles.fullRow)}>
        {renderFormTextInput({
          hasPII: true,
          inputType: 'email',
          label: 'Email',
          name: 'notaryInformation.email',
          setErrorDispatch: errorDispatch,
          validate: composeValidators(isEmptyValue('Please provide an email address.'), emailValidator),
        })}
      </div>
    </div>

    {/* PhoneNumber Row */}
    <div className={css(styles.rowContainer)}>
      <div className={css(styles.fullRow)}>
        {renderFormTextInput({
          hasPII: true,
          inputType: 'phone',
          label: 'Phone Number',
          name: 'notaryInformation.phoneNumber',
          setErrorDispatch: errorDispatch,
          validate: composeValidators((val: string) => {
            const pattern = /\(\d{3}\)\s\d{3}-\d{4}/;
            const err = val && !pattern.test(val) ? 'Please enter a complete phone number.' : undefined;
            return err;
          }, isEmptyValue('Please provide a phone number.')),
        })}
      </div>
    </div>
  </fieldset>
);
